import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  History,
  Search,
  Eye,
  Calculator,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  X,
  Archive,
  ArchiveRestore,
  Trash2,
  MoreHorizontal,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FolderOpen,
  FolderPlus,
  Folder,
  Pencil,
  FolderInput,
  Filter,
  CalendarDays,
  Building2,
  Activity,
  Timer,
  AlertTriangle,
  Save,
  Share2,
  Users,
  UserPlus,
  UserMinus,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/proposals";
import { listMySimulations, getSimulation, archiveSimulation, unarchiveSimulation, deleteSimulation, updateSimulationFolder, listMySimulationEvents, deleteSimulationEvent, shareSimulation } from "@/lib/api/simulations";
import { listProposals, deleteProposal, archiveProposal, unarchiveProposal, updateProposalMetadata } from "@/lib/api/proposals";
import { listFolders, createFolder, updateFolder, deleteFolder } from "@/lib/api/folders";
import { listSimulators } from "@/lib/api/simulators";
import { listAgencies } from "@/lib/api/agencies";
import { listShareableUsers } from "@/lib/api/users";
import { getErrorMessage } from "@/lib/api/client";
import { useUser } from "@/lib/auth/store";
import { formatDate, formatNumber } from "@/lib/utils";
import { toast } from "sonner";
import type { Simulation, SimulationWithDetails, SimulationEventUser, ProposalStatus, PricingProposal, UserFolderWithCounts, User } from "@/types";
import type { SimulationOwnershipMode } from "@/lib/api/simulations";

export const Route = createFileRoute('/_dashboard/history')({
  component: HistoryPage,
})

const FOLDER_COLORS = [
  "#6366f1", "#8b5cf6", "#ec4899", "#ef4444", "#f97316",
  "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#3b82f6",
];

interface RecentItem {
  id: number;
  type: "simulation" | "proposal";
  name: string;
  status: string;
  createdAt: string;
  simulationData?: Simulation;
  proposalData?: PricingProposal;
}

function HistoryPage() {
  const queryClient = useQueryClient();
  const currentUser = useUser();

  const [showArchived, setShowArchived] = useState(false);
  const [globalSearch, setGlobalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(globalSearch), 300);
    return () => clearTimeout(timer);
  }, [globalSearch]);

  // Filter state
  const [simulatorFilter, setSimulatorFilter] = useState<string>("all");
  const [agencyFilter, setAgencyFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const hasActiveFilters =
    simulatorFilter !== "all" ||
    agencyFilter !== "all" ||
    dateFrom !== "" ||
    dateTo !== "";

  const resetFilters = () => {
    setSimulatorFilter("all");
    setAgencyFilter("all");
    setDateFrom("");
    setDateTo("");
  };

  // Folder state
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<UserFolderWithCounts | null>(null);
  const [folderToDelete, setFolderToDelete] = useState<UserFolderWithCounts | null>(null);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLORS[0]);

  // Simulations state
  const [simulationPage, setSimulationPage] = useState(1);
  const [selectedSimulation, setSelectedSimulation] = useState<SimulationWithDetails | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [simulationToArchive, setSimulationToArchive] = useState<Simulation | null>(null);
  const [simulationToDelete, setSimulationToDelete] = useState<Simulation | null>(null);
  const [simulationOwnership, setSimulationOwnership] = useState<SimulationOwnershipMode>("owned");
  const [simulationToShare, setSimulationToShare] = useState<Simulation | null>(null);
  const [shareSearch, setShareSearch] = useState("");
  const [debouncedShareSearch, setDebouncedShareSearch] = useState("");
  const [selectedShareUserId, setSelectedShareUserId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedShareSearch(shareSearch), 250);
    return () => clearTimeout(timer);
  }, [shareSearch]);

  // Proposals state
  const [proposalPage, setProposalPage] = useState(1);
  const [proposalToArchive, setProposalToArchive] = useState<PricingProposal | null>(null);
  const [proposalToDelete, setProposalToDelete] = useState<PricingProposal | null>(null);

  // Simulation events state
  const [eventPage, setEventPage] = useState(1);
  const [selectedEvent, setSelectedEvent] = useState<SimulationEventUser | null>(null);
  const [eventDetailOpen, setEventDetailOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<SimulationEventUser | null>(null);

  const pageSize = 10;

  useEffect(() => {
    setSimulationPage(1);
    setProposalPage(1);
    setEventPage(1);
  }, [debouncedSearch, selectedFolderId, showArchived, simulatorFilter, agencyFilter, dateFrom, dateTo, simulationOwnership]);

  // ==================== Queries ====================

  const { data: folders } = useQuery({
    queryKey: ["folders"],
    queryFn: listFolders,
  });

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators-for-filter"],
    queryFn: () => listSimulators({ page_size: 100 }),
  });

  const { data: agenciesData } = useQuery({
    queryKey: ["agencies-for-filter"],
    queryFn: () => listAgencies(),
  });

  const userAgencies = useMemo(() => {
    if (!agenciesData?.items || !currentUser?.groups) return [];
    const userGroupIds = new Set(currentUser.groups.map((g) => g.id));
    return agenciesData.items.filter((a) => userGroupIds.has(a.group_id));
  }, [agenciesData?.items, currentUser?.groups]);

  const simulatorFilterId = simulatorFilter !== "all" ? parseInt(simulatorFilter) : undefined;
  const agencyFilterId = agencyFilter !== "all" ? parseInt(agencyFilter) : undefined;

  const { data: simulationsData, isLoading: simulationsLoading } = useQuery({
    queryKey: ["my-simulations", simulationOwnership, simulationPage, pageSize, debouncedSearch, selectedFolderId, showArchived, simulatorFilterId, agencyFilterId, dateFrom, dateTo],
    queryFn: () => listMySimulations({
      page: simulationPage,
      page_size: pageSize,
      // Archived recipient view is not supported - the recipient cannot
      // archive/unarchive shared cotations, so force false in shared mode.
      archived: simulationOwnership === "shared" ? false : showArchived,
      search: debouncedSearch || undefined,
      // Folders are owner-only: don't apply folder filter in shared mode.
      folder_id:
        simulationOwnership === "shared" ? undefined : (selectedFolderId ?? undefined),
      simulator_id: simulatorFilterId,
      agency_id: agencyFilterId,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      mode: simulationOwnership,
    }),
  });

  const { data: proposalsData, isLoading: proposalsLoading } = useQuery({
    queryKey: ["proposals", proposalPage, pageSize, debouncedSearch, selectedFolderId, showArchived, simulatorFilterId, agencyFilterId, dateFrom, dateTo],
    queryFn: () =>
      listProposals({
        page: proposalPage,
        page_size: pageSize,
        search: debouncedSearch || undefined,
        folder_id: selectedFolderId ?? undefined,
        archived: showArchived,
        simulator_id: simulatorFilterId,
        agency_id: agencyFilterId,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
  });

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ["my-simulation-events", eventPage, pageSize, debouncedSearch, simulatorFilterId, dateFrom, dateTo],
    queryFn: () => listMySimulationEvents({
      page: eventPage,
      page_size: pageSize,
      simulator_id: simulatorFilterId,
      search: debouncedSearch || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
  });

  const { data: recentSimulations } = useQuery({
    queryKey: ["my-simulations-recent"],
    queryFn: () => listMySimulations(1, 5, false),
  });

  const { data: recentProposals } = useQuery({
    queryKey: ["proposals-recent"],
    queryFn: () => listProposals({ page: 1, page_size: 5 }),
  });

  const { data: shareableUsers, isFetching: shareableUsersLoading } = useQuery({
    queryKey: ["shareable-users", debouncedShareSearch],
    queryFn: () => listShareableUsers(debouncedShareSearch || undefined),
    enabled: simulationToShare !== null,
  });

  const recentItems = useMemo<RecentItem[]>(() => {
    const items: RecentItem[] = [];

    recentSimulations?.items.forEach((sim) => {
      items.push({
        id: sim.id,
        type: "simulation",
        name: sim.simulation_ref || sim.name || `Cotation #${sim.id}`,
        status: sim.status,
        createdAt: sim.created_at,
        simulationData: sim,
      });
    });

    recentProposals?.items.forEach((prop) => {
      items.push({
        id: prop.id,
        type: "proposal",
        name: prop.name,
        status: prop.status,
        createdAt: prop.created_at,
        proposalData: prop,
      });
    });

    return items.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ).slice(0, 10);
  }, [recentSimulations, recentProposals]);

  // ==================== Mutations ====================

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["my-simulations"] });
    queryClient.invalidateQueries({ queryKey: ["proposals"] });
    queryClient.invalidateQueries({ queryKey: ["folders"] });
  };

  // -- Simulation mutations --
  const archiveSimulationMutation = useMutation({
    mutationFn: (simulationId: number) => archiveSimulation(simulationId),
    onSuccess: () => { toast.success("Cotation archivée"); invalidateAll(); setSimulationToArchive(null); },
    onError: () => { toast.error("Erreur lors de l'archivage"); },
  });

  const unarchiveSimulationMutation = useMutation({
    mutationFn: (simulationId: number) => unarchiveSimulation(simulationId),
    onSuccess: () => { toast.success("Cotation restaurée"); invalidateAll(); },
    onError: () => { toast.error("Erreur lors de la restauration"); },
  });

  const deleteSimulationMutation = useMutation({
    mutationFn: (simulationId: number) => deleteSimulation(simulationId),
    onSuccess: () => { toast.success("Cotation supprimée définitivement"); invalidateAll(); setSimulationToDelete(null); },
    onError: () => { toast.error("Erreur lors de la suppression"); },
  });

  const moveSimulationToFolderMutation = useMutation({
    mutationFn: ({ simulationId, folderId }: { simulationId: number; folderId: number | null }) =>
      updateSimulationFolder(simulationId, folderId),
    onSuccess: () => { toast.success("Cotation déplacée"); invalidateAll(); },
    onError: () => { toast.error("Erreur lors du déplacement"); },
  });

  const shareSimulationMutation = useMutation({
    mutationFn: ({ simulationId, userId }: { simulationId: number; userId: number | null }) =>
      shareSimulation(simulationId, userId),
    onSuccess: (_, vars) => {
      toast.success(vars.userId === null ? "Partage retiré" : "Cotation partagée");
      invalidateAll();
      setSimulationToShare(null);
      setSelectedShareUserId(null);
      setShareSearch("");
    },
    onError: (error) => {
      toast.error("Erreur lors du partage", { description: getErrorMessage(error) });
    },
  });

  // -- Proposal mutations --
  const archiveProposalMutation = useMutation({
    mutationFn: (proposalId: number) => archiveProposal(proposalId),
    onSuccess: () => { toast.success("Proposition archivée"); invalidateAll(); setProposalToArchive(null); },
    onError: () => { toast.error("Erreur lors de l'archivage"); },
  });

  const unarchiveProposalMutation = useMutation({
    mutationFn: (proposalId: number) => unarchiveProposal(proposalId),
    onSuccess: () => { toast.success("Proposition restaurée"); invalidateAll(); },
    onError: () => { toast.error("Erreur lors de la restauration"); },
  });

  const deleteProposalMutation = useMutation({
    mutationFn: (id: number) => deleteProposal(id),
    onSuccess: () => { toast.success("Proposition supprimée"); invalidateAll(); setProposalToDelete(null); },
    onError: (error) => { toast.error("Erreur", { description: getErrorMessage(error) }); },
  });

  const moveProposalToFolderMutation = useMutation({
    mutationFn: ({ proposalId, folderId }: { proposalId: number; folderId: number | null }) =>
      updateProposalMetadata(proposalId, { folder_id: folderId }),
    onSuccess: () => { toast.success("Proposition déplacée"); invalidateAll(); },
    onError: () => { toast.error("Erreur lors du déplacement"); },
  });

  // -- Simulation event mutations --
  const deleteEventMutation = useMutation({
    mutationFn: (eventId: number) => deleteSimulationEvent(eventId),
    onSuccess: () => {
      toast.success("Simulation supprimée");
      queryClient.invalidateQueries({ queryKey: ["my-simulation-events"] });
      setEventToDelete(null);
    },
    onError: () => { toast.error("Erreur lors de la suppression"); },
  });

  // -- Folder mutations --
  const createFolderMutation = useMutation({
    mutationFn: () => createFolder({ name: newFolderName, color: newFolderColor }),
    onSuccess: () => {
      toast.success("Dossier créé");
      invalidateAll();
      setCreateFolderOpen(false);
      setNewFolderName("");
      setNewFolderColor(FOLDER_COLORS[0]);
    },
    onError: () => { toast.error("Erreur lors de la création"); },
  });

  const updateFolderMutation = useMutation({
    mutationFn: () => {
      if (!editingFolder) throw new Error("No folder");
      return updateFolder(editingFolder.id, { name: newFolderName, color: newFolderColor });
    },
    onSuccess: () => {
      toast.success("Dossier modifié");
      invalidateAll();
      setEditingFolder(null);
    },
    onError: () => { toast.error("Erreur lors de la modification"); },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: number) => deleteFolder(folderId),
    onSuccess: () => {
      toast.success("Dossier supprimé");
      if (selectedFolderId === folderToDelete?.id) setSelectedFolderId(null);
      invalidateAll();
      setFolderToDelete(null);
    },
    onError: () => { toast.error("Erreur lors de la suppression"); },
  });

  // ==================== Handlers ====================

  const filteredSimulations = simulationsData?.items ?? [];
  const filteredProposals = proposalsData?.items ?? [];

  const handleViewDetail = async (simulation: Simulation) => {
    try {
      const detail = await getSimulation(simulation.id);
      setSelectedSimulation(detail);
      setDetailDialogOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenInSimulator = () => {
    if (!selectedSimulation || !selectedSimulation.simulator_id) return;
    sessionStorage.setItem(
      `simulation_inputs_${selectedSimulation.id}`,
      JSON.stringify(selectedSimulation.inputs || {})
    );
    const url = `/simulators/${selectedSimulation.simulator_id}/simulate?version=${selectedSimulation.version_id}&loadSimulation=${selectedSimulation.id}&t=${Date.now()}`;
    window.open(url, '_blank');
  };

  const handleOpenEventInSimulator = (event: SimulationEventUser) => {
    if (!event.simulator_id || !event.version_id) {
      toast.error("Cette simulation ne peut pas être rouverte (simulateur ou version inconnu)");
      return;
    }
    sessionStorage.setItem(
      `simulation_event_inputs_${event.id}`,
      JSON.stringify(event.inputs || {})
    );
    const url = `/simulators/${event.simulator_id}/simulate?version=${event.version_id}&loadEvent=${event.id}&t=${Date.now()}`;
    window.open(url, '_blank');
  };

  const openEditFolder = (folder: UserFolderWithCounts) => {
    setNewFolderName(folder.name);
    setNewFolderColor(folder.color || FOLDER_COLORS[0]);
    setEditingFolder(folder);
  };

  const getSimulationStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return (
          <Badge variant="success" className="gap-1">
            <CheckCircle className="h-3 w-3" />
            Terminée
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive" className="gap-1">
            <XCircle className="h-3 w-3" />
            Échouée
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="gap-1">
            <Clock className="h-3 w-3" />
            En cours
          </Badge>
        );
    }
  };

  // ==================== Folder sub-menu for dropdowns ====================

  const FolderSubMenu = ({ currentFolderId, onMove }: { currentFolderId: number | null; onMove: (folderId: number | null) => void }) => (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2">
        <FolderInput className="h-4 w-4" />
        Déplacer dans un dossier
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {currentFolderId !== null && (
          <>
            <DropdownMenuItem onClick={() => onMove(null)} className="gap-2">
              <X className="h-4 w-4" />
              Retirer du dossier
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {folders && folders.length > 0 ? (
          folders.map((f) => (
            <DropdownMenuItem
              key={f.id}
              onClick={() => onMove(f.id)}
              disabled={f.id === currentFolderId}
              className="gap-2"
            >
              <div className="h-3 w-3 rounded-sm flex-shrink-0" style={{ backgroundColor: f.color || "#6366f1" }} />
              {f.name}
            </DropdownMenuItem>
          ))
        ) : (
          <DropdownMenuItem disabled className="text-xs text-muted-foreground">
            Aucun dossier
          </DropdownMenuItem>
        )}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );

  const getClientInfo = (inputs?: Record<string, unknown>) => {
    const rs = inputs?.["raison_sociale"] ?? inputs?.["raison_social"];
    const cc = inputs?.["coclico"];
    return {
      raisonSociale: rs ? String(rs) : null,
      coclico: cc ? String(cc) : null,
    };
  };

  // ==================== Render ====================

  return (
    <div className="flex flex-col h-full">
      <Header title="Mon Historique" />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Global Search + Archive Filter */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher (nom, coclico, raison sociale...)"
                  value={globalSearch}
                  onChange={(e) => setGlobalSearch(e.target.value)}
                  className="pl-10"
                />
                {globalSearch && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                    onClick={() => setGlobalSearch("")}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="flex items-center rounded-lg border bg-muted/50 p-0.5">
                <Button
                  variant={!showArchived ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setShowArchived(false)}
                  className="gap-1.5 h-8"
                >
                  <History className="h-3.5 w-3.5" />
                  Actives
                </Button>
                <Button
                  variant={showArchived ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setShowArchived(true)}
                  className="gap-1.5 h-8"
                >
                  <Archive className="h-3.5 w-3.5" />
                  Archivées
                </Button>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="flex flex-wrap items-center gap-3 pt-1 border-t">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Filter className="h-4 w-4" />
                <span className="text-sm font-medium">Filtres</span>
              </div>

              <Select
                value={simulatorFilter}
                onValueChange={setSimulatorFilter}
              >
                <SelectTrigger className="w-[180px] h-9">
                  <SelectValue placeholder="Offre" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les offres</SelectItem>
                  {simulatorsData?.items.map((sim) => (
                    <SelectItem key={sim.id} value={String(sim.id)}>
                      {sim.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={agencyFilter}
                onValueChange={setAgencyFilter}
              >
                <SelectTrigger className="w-[180px] h-9">
                  <Building2 className="h-4 w-4 mr-1.5 text-muted-foreground flex-shrink-0" />
                  <SelectValue placeholder="Agence" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les agences</SelectItem>
                  {userAgencies.map((agency) => (
                    <SelectItem key={agency.id} value={String(agency.id)}>
                      {agency.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <div className="flex items-center gap-1.5">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 w-[140px] text-sm"
                  />
                  <span className="text-xs text-muted-foreground">–</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 w-[140px] text-sm"
                  />
                </div>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="gap-1.5 h-9"
                >
                  <X className="h-3.5 w-3.5" />
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Folders Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={selectedFolderId === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFolderId(null)}
                className="gap-1.5"
              >
                <FolderOpen className="h-4 w-4" />
                Tous
              </Button>

              {folders?.map((folder) => (
                <div key={folder.id} className="group relative">
                  <Button
                    variant={selectedFolderId === folder.id ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedFolderId(folder.id)}
                    className="gap-1.5 pr-2"
                  >
                    <div
                      className="h-3 w-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: folder.color || "#6366f1" }}
                    />
                    {folder.name}
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                      {folder.simulation_count + folder.proposal_count}
                    </Badge>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-background border shadow-sm"
                      >
                        <MoreHorizontal className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditFolder(folder)} className="gap-2">
                        <Pencil className="h-4 w-4" />
                        Modifier
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setFolderToDelete(folder)}
                        className="gap-2 text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setNewFolderName(""); setNewFolderColor(FOLDER_COLORS[0]); setCreateFolderOpen(true); }}
                className="gap-1.5 border border-dashed"
              >
                <FolderPlus className="h-4 w-4" />
                Nouveau dossier
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Items Section */}
        {selectedFolderId === null && !debouncedSearch && !showArchived && !hasActiveFilters && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Mes plus récents
              </CardTitle>
              <CardDescription>
                Vos dernières cotations et propositions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full whitespace-nowrap">
                <div className="flex gap-4 pb-4">
                  {recentItems.length === 0 ? (
                    <div className="flex items-center justify-center w-full py-8 text-muted-foreground">
                      <p>Aucun document récent</p>
                    </div>
                  ) : (
                    recentItems.map((item) => (
                      <Card
                        key={`${item.type}-${item.id}`}
                        className="min-w-[280px] max-w-[280px] cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => {
                          if (item.type === "simulation" && item.simulationData) {
                            handleViewDetail(item.simulationData);
                          } else if (item.type === "proposal") {
                            window.open(`/proposals/${item.id}`, '_blank');
                          }
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              {item.type === "simulation" ? (
                                <Calculator className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              ) : (
                                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              )}
                              <span className="font-medium truncate">{item.name}</span>
                            </div>
                            <Badge variant="outline" className="flex-shrink-0">
                              {item.type === "simulation" ? "Cotation" : "Proposition"}
                            </Badge>
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            {item.type === "simulation" ? (
                              getSimulationStatusBadge(item.status)
                            ) : (
                              <StatusBadge status={item.status as ProposalStatus} size="sm" />
                            )}
                            <span className="text-xs text-muted-foreground">
                              {formatDate(item.createdAt)}
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Simulations Column */}
          <Card className="flex flex-col">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  {simulationOwnership === "owned" ? "Mes Cotations" : "Partagées avec moi"}
                </CardTitle>
                <div className="flex items-center rounded-lg border bg-muted/50 p-0.5">
                  <Button
                    variant={simulationOwnership === "owned" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSimulationOwnership("owned")}
                    className="gap-1.5 h-8"
                  >
                    <Users className="h-3.5 w-3.5" />
                    Mes cotations
                  </Button>
                  <Button
                    variant={simulationOwnership === "shared" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setSimulationOwnership("shared")}
                    className="gap-1.5 h-8"
                  >
                    <Share2 className="h-3.5 w-3.5" />
                    Partagées avec moi
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 space-y-4">
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {simulationsLoading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                        </TableRow>
                      ))
                    ) : filteredSimulations.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            {simulationOwnership === "shared" ? (
                              <Share2 className="h-8 w-8 opacity-50" />
                            ) : showArchived ? (
                              <Archive className="h-8 w-8 opacity-50" />
                            ) : (
                              <History className="h-8 w-8 opacity-50" />
                            )}
                            <p className="text-sm">
                              {simulationOwnership === "shared"
                                ? "Aucune cotation partagée avec vous"
                                : showArchived
                                  ? "Aucune cotation archivée"
                                  : "Aucune cotation"}
                            </p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSimulations.map((simulation) => {
                        const isOwnedView = simulationOwnership === "owned";
                        const sharedWithLabel =
                          simulation.shared_with_user_full_name ||
                          simulation.shared_with_user_email;
                        const sharedByLabel =
                          simulation.shared_by_user_full_name ||
                          simulation.shared_by_user_email;
                        return (
                        <TableRow key={simulation.id}>
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <div className="flex items-center gap-2">
                                <Calculator className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="truncate max-w-[150px]" title={simulation.name || `Cotation #${simulation.id}`}>
                                  {simulation.name || `Cotation #${simulation.id}`}
                                </span>
                              </div>
                              {simulation.simulation_ref && (
                                <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px] pl-6" title={simulation.simulation_ref}>
                                  {simulation.simulation_ref}
                                </span>
                              )}
                              {(() => {
                                const { raisonSociale, coclico } = getClientInfo(simulation.inputs);
                                return (
                                  <>
                                    {raisonSociale && (
                                      <span className="text-xs text-muted-foreground truncate max-w-[200px] pl-6" title={raisonSociale}>
                                        {raisonSociale}
                                      </span>
                                    )}
                                    {coclico && (
                                      <span className="text-xs text-muted-foreground truncate max-w-[200px] pl-6" title={`Coclico : ${coclico}`}>
                                        Coclico : {coclico}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                              {simulation.agency_names && simulation.agency_names.length > 0 && (
                                <div className="flex flex-wrap gap-1 pl-6 mt-0.5">
                                  {simulation.agency_names.map((name) => (
                                    <Badge key={name} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                      {name}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              {isOwnedView && sharedWithLabel && (
                                <Badge
                                  variant="secondary"
                                  className="ml-6 mt-0.5 w-fit gap-1 text-[10px] px-1.5 py-0 h-4"
                                  title={`Partagée avec ${sharedWithLabel}`}
                                >
                                  <Share2 className="h-3 w-3" />
                                  Partagée avec {sharedWithLabel}
                                </Badge>
                              )}
                              {!isOwnedView && sharedByLabel && (
                                <Badge
                                  variant="secondary"
                                  className="ml-6 mt-0.5 w-fit gap-1 text-[10px] px-1.5 py-0 h-4"
                                  title={`Partagée par ${sharedByLabel}`}
                                >
                                  <UserPlus className="h-3 w-3" />
                                  Partagée par {sharedByLabel}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(simulation.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewDetail(simulation)}
                                title="Voir les détails"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isOwnedView ? (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedShareUserId(simulation.shared_with_user_id);
                                        setShareSearch("");
                                        setSimulationToShare(simulation);
                                      }}
                                      className="gap-2"
                                    >
                                      <Share2 className="h-4 w-4" />
                                      {simulation.shared_with_user_id
                                        ? "Modifier le partage"
                                        : "Partager"}
                                    </DropdownMenuItem>
                                    {simulation.shared_with_user_id && (
                                      <DropdownMenuItem
                                        onClick={() =>
                                          shareSimulationMutation.mutate({
                                            simulationId: simulation.id,
                                            userId: null,
                                          })
                                        }
                                        className="gap-2"
                                      >
                                        <UserMinus className="h-4 w-4" />
                                        Retirer le partage
                                      </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <FolderSubMenu
                                      currentFolderId={simulation.folder_id}
                                      onMove={(folderId) => moveSimulationToFolderMutation.mutate({ simulationId: simulation.id, folderId })}
                                    />
                                    <DropdownMenuSeparator />
                                    {!simulation.is_archived ? (
                                      <DropdownMenuItem
                                        onClick={() => setSimulationToArchive(simulation)}
                                        className="gap-2"
                                      >
                                        <Archive className="h-4 w-4" />
                                        Archiver
                                      </DropdownMenuItem>
                                    ) : (
                                      <>
                                        <DropdownMenuItem
                                          onClick={() => unarchiveSimulationMutation.mutate(simulation.id)}
                                          className="gap-2"
                                        >
                                          <ArchiveRestore className="h-4 w-4" />
                                          Restaurer
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={() => setSimulationToDelete(simulation)}
                                          className="gap-2 text-destructive focus:text-destructive"
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Supprimer définitivement
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {simulationsData && simulationsData.total_pages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {simulationsData.total_items} cotation{simulationsData.total_items > 1 ? "s" : ""}
                  </p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="icon" onClick={() => setSimulationPage((p) => Math.max(1, p - 1))} disabled={simulationPage === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center px-3 py-1 bg-muted rounded text-sm">
                      {simulationPage} / {simulationsData.total_pages}
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setSimulationPage((p) => Math.min(simulationsData.total_pages, p + 1))} disabled={simulationPage === simulationsData.total_pages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Proposals Column */}
          <Card className="flex flex-col">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Mes Propositions
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 space-y-4">
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposalsLoading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                        </TableRow>
                      ))
                    ) : filteredProposals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="h-24 text-center">
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            {showArchived ? (
                              <Archive className="h-8 w-8 opacity-50" />
                            ) : (
                              <FileText className="h-8 w-8 opacity-50" />
                            )}
                            <p className="text-sm">
                              {showArchived ? "Aucune proposition archivée" : "Aucune proposition"}
                            </p>
                            {!showArchived && (
                              <Button asChild variant="outline" size="sm" className="mt-1">
                                <Link to="/simulators">Créer une cotation</Link>
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredProposals.map((proposal) => (
                        <TableRow key={proposal.id} className="group">
                          <TableCell>
                            <div className="flex flex-col gap-0.5">
                              <a
                                href={`/proposals/${proposal.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium hover:underline flex items-center gap-2"
                              >
                                <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <span className="truncate max-w-[150px]">{proposal.name}</span>
                              </a>
                              {proposal.raison_sociale && (
                                <span className="text-xs text-muted-foreground truncate max-w-[200px] pl-6" title={proposal.raison_sociale}>
                                  {proposal.raison_sociale}
                                </span>
                              )}
                              {proposal.coclico && (
                                <span className="text-xs text-muted-foreground truncate max-w-[200px] pl-6" title={`Coclico : ${proposal.coclico}`}>
                                  Coclico : {proposal.coclico}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {formatDate(proposal.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" asChild title="Voir/Éditer">
                                <a href={`/proposals/${proposal.id}`} target="_blank" rel="noopener noreferrer">
                                  <Eye className="h-4 w-4" />
                                </a>
                              </Button>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <FolderSubMenu
                                    currentFolderId={proposal.folder_id}
                                    onMove={(folderId) => moveProposalToFolderMutation.mutate({ proposalId: proposal.id, folderId })}
                                  />
                                  <DropdownMenuSeparator />
                                  {!proposal.is_archived ? (
                                    <DropdownMenuItem
                                      onClick={() => setProposalToArchive(proposal)}
                                      className="gap-2"
                                    >
                                      <Archive className="h-4 w-4" />
                                      Archiver
                                    </DropdownMenuItem>
                                  ) : (
                                    <>
                                      <DropdownMenuItem
                                        onClick={() => unarchiveProposalMutation.mutate(proposal.id)}
                                        className="gap-2"
                                      >
                                        <ArchiveRestore className="h-4 w-4" />
                                        Restaurer
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => setProposalToDelete(proposal)}
                                        className="gap-2 text-destructive focus:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Supprimer définitivement
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {proposalsData && proposalsData.total_pages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {proposalsData.total_items} proposition{proposalsData.total_items > 1 ? "s" : ""}
                  </p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="icon" onClick={() => setProposalPage((p) => Math.max(1, p - 1))} disabled={proposalPage === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex items-center px-3 py-1 bg-muted rounded text-sm">
                      {proposalPage} / {proposalsData.total_pages}
                    </div>
                    <Button variant="outline" size="icon" onClick={() => setProposalPage((p) => Math.min(proposalsData.total_pages, p + 1))} disabled={proposalPage === proposalsData.total_pages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Simulation Events Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Mes Simulations
            </CardTitle>
            <CardDescription>
              Historique de tous vos calculs
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Simulateur</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="w-[140px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventsLoading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-8 w-24" /></TableCell>
                      </TableRow>
                    ))
                  ) : !eventsData?.items.length ? (
                    <TableRow>
                      <TableCell colSpan={3} className="h-24 text-center">
                        <div className="flex flex-col items-center gap-2 text-muted-foreground">
                          <Activity className="h-8 w-8 opacity-50" />
                          <p className="text-sm">Aucune simulation</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    eventsData.items.map((event) => (
                      <TableRow key={event.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedEvent(event); setEventDetailOpen(true); }}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Calculator className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex flex-col gap-0.5">
                              <span className="truncate max-w-[200px]" title={event.simulator_name}>
                                {event.simulator_name}
                              </span>
                              {event.simulation_name && (
                                <span className="text-xs font-medium truncate max-w-[200px]" title={event.simulation_name}>
                                  {event.simulation_name}
                                </span>
                              )}
                              {event.simulation_ref && (
                                <span className="text-xs text-muted-foreground font-mono truncate max-w-[200px]" title={event.simulation_ref}>
                                  {event.simulation_ref}
                                </span>
                              )}
                              {/* Sans cotation, une simulation n'a pas de nom : ce qui a été tapé dans le formulaire en tient lieu */}
                              {event.saisies_libres && event.saisies_libres.length > 0 && (
                                <span
                                  className={`text-xs truncate max-w-[200px] lg:max-w-[420px] ${event.simulation_name ? "text-muted-foreground" : "font-medium"}`}
                                  title={event.saisies_libres.join(" · ")}
                                >
                                  {event.saisies_libres.join(" · ")}
                                </span>
                              )}
                              {(() => {
                                const { raisonSociale, coclico } = getClientInfo(event.inputs);
                                return (
                                  <>
                                    {raisonSociale && (
                                      <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={raisonSociale}>
                                        {raisonSociale}
                                      </span>
                                    )}
                                    {coclico && (
                                      <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={`Coclico : ${coclico}`}>
                                        Coclico : {coclico}
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                              {event.version_number && !event.simulation_name && (
                                <span className="text-xs text-muted-foreground">
                                  v{event.version_number}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(event.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); setEventDetailOpen(true); }}
                              title="Voir les détails"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); handleOpenEventInSimulator(event); }}
                              title="Ouvrir dans le simulateur"
                              disabled={!event.simulator_id || !event.version_id}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); setEventToDelete(event); }}
                              title="Supprimer"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {eventsData && eventsData.total_pages > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {eventsData.total_items} simulation{eventsData.total_items > 1 ? "s" : ""}
                </p>
                <div className="flex gap-1">
                  <Button variant="outline" size="icon" onClick={() => setEventPage((p) => Math.max(1, p - 1))} disabled={eventPage === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center px-3 py-1 bg-muted rounded text-sm">
                    {eventPage} / {eventsData.total_pages}
                  </div>
                  <Button variant="outline" size="icon" onClick={() => setEventPage((p) => Math.min(eventsData.total_pages, p + 1))} disabled={eventPage === eventsData.total_pages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ==================== Dialogs ==================== */}

      {/* Simulation Event Detail Dialog */}
      <Dialog open={eventDetailOpen} onOpenChange={setEventDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {selectedEvent?.simulation_name || "Détail de la simulation"}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent?.simulator_name}
              {selectedEvent?.version_number && ` - Version ${selectedEvent.version_number}`}
            </DialogDescription>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                {selectedEvent.success ? (
                  <Badge variant="success" className="gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Succès
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1">
                    <XCircle className="h-3 w-3" />
                    Échec
                  </Badge>
                )}
                {selectedEvent.is_save ? (
                  <Badge variant="secondary" className="gap-1">
                    <Save className="h-3 w-3" />
                    Sauvegarde
                  </Badge>
                ) : (
                  <Badge variant="outline" className="gap-1">
                    <Calculator className="h-3 w-3" />
                    Calcul
                  </Badge>
                )}
                {selectedEvent.simulation_ref && (
                  <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                    {selectedEvent.simulation_ref}
                  </span>
                )}
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Timer className="h-4 w-4" />
                  {selectedEvent.execution_time_ms}ms
                </span>
                <span className="text-muted-foreground">
                  {formatDate(selectedEvent.created_at)}
                </span>
              </div>

              {selectedEvent.errors.length > 0 && (
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 space-y-1">
                  <p className="text-sm font-medium flex items-center gap-1.5 text-destructive">
                    <XCircle className="h-4 w-4" />
                    Erreurs
                  </p>
                  {selectedEvent.errors.map((err, i) => (
                    <p key={i} className="text-sm text-destructive/80">{err}</p>
                  ))}
                </div>
              )}

              {selectedEvent.warnings.length > 0 && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 space-y-1">
                  <p className="text-sm font-medium flex items-center gap-1.5 text-yellow-600 dark:text-yellow-400">
                    <AlertTriangle className="h-4 w-4" />
                    Avertissements
                  </p>
                  {selectedEvent.warnings.map((warn, i) => (
                    <p key={i} className="text-sm text-yellow-600/80 dark:text-yellow-400/80">{warn}</p>
                  ))}
                </div>
              )}

              <Collapsible defaultOpen={false}>
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:bg-muted/50 rounded px-2 -mx-2 group">
                  <span>Entrées ({Object.keys(selectedEvent.inputs || {}).length})</span>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {Object.entries(selectedEvent.inputs || {}).map(([key, value]) => (
                      <div key={key} className="p-2 rounded bg-muted/50 text-sm">
                        <span className="text-muted-foreground">{key}:</span>{" "}
                        <span className="font-mono">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible defaultOpen={false}>
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:bg-muted/50 rounded px-2 -mx-2 group">
                  <span>Sorties ({Object.keys(selectedEvent.outputs || {}).length})</span>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {Object.entries(selectedEvent.outputs || {}).map(([key, value]) => (
                      <div key={key} className="p-3 rounded bg-primary/10 border border-primary/20">
                        <p className="text-sm text-muted-foreground">{key}</p>
                        <p className="text-lg font-bold text-primary">
                          {typeof value === "number" ? formatNumber(value) : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Simulation Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                {selectedSimulation?.name || `Cotation #${selectedSimulation?.id}`}
              </DialogTitle>
              {selectedSimulation?.simulator_id && (
                <Button variant="default" onClick={handleOpenInSimulator} className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Ouvrir dans le simulateur
                </Button>
              )}
            </div>
            <DialogDescription>
              {selectedSimulation?.simulation_ref && (
                <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded mr-2">
                  {selectedSimulation.simulation_ref}
                </span>
              )}
              {selectedSimulation?.simulator_name ? (
                <span>
                  {selectedSimulation.simulator_name} - Version{" "}
                  {selectedSimulation.version_number}
                </span>
              ) : (
                <span>Cotation #{selectedSimulation?.id}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedSimulation && (
            <div className="space-y-6">
              <div className="flex items-center gap-4 text-sm">
                {getSimulationStatusBadge(selectedSimulation.status)}
                {selectedSimulation.is_archived && (
                  <Badge variant="outline" className="gap-1">
                    <Archive className="h-3 w-3" />
                    Archivée
                  </Badge>
                )}
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {selectedSimulation.execution_time_ms}ms
                </span>
                <span className="text-muted-foreground">
                  {formatDate(selectedSimulation.created_at)}
                </span>
              </div>

              {selectedSimulation.agency_names && selectedSimulation.agency_names.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">Agence(s) :</span>
                  {selectedSimulation.agency_names.map((name) => (
                    <Badge key={name} variant="secondary">{name}</Badge>
                  ))}
                </div>
              )}

              {selectedSimulation.shared_with_user_id && (
                <div className="flex items-center gap-2 text-sm">
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Partagée avec :</span>
                  <Badge variant="secondary">
                    {selectedSimulation.shared_with_user_full_name ||
                      selectedSimulation.shared_with_user_email}
                  </Badge>
                </div>
              )}

              {selectedSimulation.shared_by_user_email && (
                <div className="flex items-center gap-2 text-sm">
                  <UserPlus className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Partagée par :</span>
                  <Badge variant="secondary">
                    {selectedSimulation.shared_by_user_full_name ||
                      selectedSimulation.shared_by_user_email}
                  </Badge>
                </div>
              )}

              {selectedSimulation.notes && (
                <div className="p-3 rounded-lg bg-muted/50">
                  <p className="text-sm font-medium mb-1">Notes</p>
                  <p className="text-sm text-muted-foreground">{selectedSimulation.notes}</p>
                </div>
              )}

              <Collapsible defaultOpen={false}>
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:bg-muted/50 rounded px-2 -mx-2 group">
                  <span>Entrées ({Object.keys(selectedSimulation.inputs || {}).length})</span>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {Object.entries(selectedSimulation.inputs || {}).map(([key, value]) => (
                      <div key={key} className="p-2 rounded bg-muted/50 text-sm">
                        <span className="text-muted-foreground">{key}:</span>{" "}
                        <span className="font-mono">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <Collapsible defaultOpen={false}>
                <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-sm font-medium hover:bg-muted/50 rounded px-2 -mx-2 group">
                  <span>Sorties ({Object.keys(selectedSimulation.outputs || {}).length})</span>
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    {Object.entries(selectedSimulation.outputs || {}).map(([key, value]) => (
                      <div key={key} className="p-3 rounded bg-primary/10 border border-primary/20">
                        <p className="text-sm text-muted-foreground">{key}</p>
                        <p className="text-lg font-bold text-primary">
                          {typeof value === "number" ? formatNumber(value) : String(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Simulation Dialog */}
      <Dialog
        open={!!simulationToShare}
        onOpenChange={(open) => {
          if (!open) {
            setSimulationToShare(null);
            setSelectedShareUserId(null);
            setShareSearch("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="h-5 w-5" />
              Partager la cotation
            </DialogTitle>
            <DialogDescription>
              {simulationToShare?.name || (simulationToShare ? `Cotation #${simulationToShare.id}` : "")}
              <span className="block text-xs mt-1">
                Le destinataire pourra consulter et modifier la cotation. L&apos;archivage et la suppression
                restent réservés au créateur. Un seul destinataire à la fois ; un nouveau partage remplace l&apos;ancien.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {simulationToShare?.shared_with_user_id && (
              <div className="flex items-center justify-between gap-2 rounded-lg border bg-muted/50 px-3 py-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <Share2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">
                    Actuellement partagée avec{" "}
                    <strong>
                      {simulationToShare.shared_with_user_full_name ||
                        simulationToShare.shared_with_user_email}
                    </strong>
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    shareSimulationMutation.mutate({
                      simulationId: simulationToShare.id,
                      userId: null,
                    })
                  }
                  disabled={shareSimulationMutation.isPending}
                  className="gap-1.5 h-7 text-destructive hover:text-destructive"
                >
                  <UserMinus className="h-3.5 w-3.5" />
                  Retirer
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="share-search">Rechercher un utilisateur</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="share-search"
                  value={shareSearch}
                  onChange={(e) => setShareSearch(e.target.value)}
                  placeholder="Nom, email, pseudo..."
                  className="pl-10"
                  autoFocus
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Seuls les utilisateurs actifs partageant un de vos groupes apparaissent.
              </p>
            </div>

            <div className="border rounded-lg max-h-[260px] overflow-y-auto">
              {shareableUsersLoading ? (
                <div className="p-3 space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : !shareableUsers || shareableUsers.length === 0 ? (
                <p className="p-4 text-sm text-center text-muted-foreground">
                  Aucun utilisateur trouvé
                </p>
              ) : (
                <ul className="divide-y">
                  {shareableUsers.map((u: User) => {
                    const isSelected = selectedShareUserId === u.id;
                    const isCurrent = simulationToShare?.shared_with_user_id === u.id;
                    return (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => setSelectedShareUserId(u.id)}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50 transition-colors ${
                            isSelected ? "bg-primary/10" : ""
                          }`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <UserPlus className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex flex-col min-w-0">
                              <span className="truncate font-medium">
                                {u.full_name || u.email}
                              </span>
                              {u.full_name && (
                                <span className="text-xs text-muted-foreground truncate">
                                  {u.email}
                                </span>
                              )}
                            </div>
                          </div>
                          {isCurrent && (
                            <Badge variant="outline" className="text-[10px] flex-shrink-0">
                              actuel
                            </Badge>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSimulationToShare(null);
                setSelectedShareUserId(null);
                setShareSearch("");
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (!simulationToShare || selectedShareUserId === null) return;
                shareSimulationMutation.mutate({
                  simulationId: simulationToShare.id,
                  userId: selectedShareUserId,
                });
              }}
              disabled={
                selectedShareUserId === null ||
                selectedShareUserId === simulationToShare?.shared_with_user_id ||
                shareSimulationMutation.isPending
              }
              className="gap-1.5"
            >
              <Share2 className="h-4 w-4" />
              {shareSimulationMutation.isPending ? "Partage..." : "Partager"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Simulation Confirmation */}
      <AlertDialog open={!!simulationToArchive} onOpenChange={(open) => !open && setSimulationToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver cette cotation ?</AlertDialogTitle>
            <AlertDialogDescription>
              La cotation &quot;{simulationToArchive?.name || `#${simulationToArchive?.id}`}&quot; sera archivée.
              Vous pourrez la restaurer depuis le menu contextuel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => simulationToArchive && archiveSimulationMutation.mutate(simulationToArchive.id)}
              disabled={archiveSimulationMutation.isPending}
            >
              {archiveSimulationMutation.isPending ? "Archivage..." : "Archiver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Simulation Confirmation */}
      <AlertDialog open={!!simulationToDelete} onOpenChange={(open) => !open && setSimulationToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La cotation &quot;{simulationToDelete?.name || `#${simulationToDelete?.id}`}&quot;
              et toutes ses données associées seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => simulationToDelete && deleteSimulationMutation.mutate(simulationToDelete.id)}
              disabled={deleteSimulationMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSimulationMutation.isPending ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Proposal Confirmation */}
      <AlertDialog open={!!proposalToArchive} onOpenChange={(open) => !open && setProposalToArchive(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archiver cette proposition ?</AlertDialogTitle>
            <AlertDialogDescription>
              La proposition &quot;{proposalToArchive?.name}&quot; sera archivée.
              Vous pourrez la restaurer depuis le menu contextuel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => proposalToArchive && archiveProposalMutation.mutate(proposalToArchive.id)}
              disabled={archiveProposalMutation.isPending}
            >
              {archiveProposalMutation.isPending ? "Archivage..." : "Archiver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Proposal Confirmation */}
      <AlertDialog open={!!proposalToDelete} onOpenChange={() => setProposalToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La proposition &quot;{proposalToDelete?.name}&quot;
              et toutes ses versions seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => proposalToDelete && deleteProposalMutation.mutate(proposalToDelete.id)}
              disabled={deleteProposalMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteProposalMutation.isPending ? "Suppression..." : "Supprimer définitivement"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Simulation Event Confirmation */}
      <AlertDialog open={!!eventToDelete} onOpenChange={(open) => !open && setEventToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">Supprimer cette simulation ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La simulation sera retirée de votre historique.
              {eventToDelete?.simulation_id && (
                <span className="block mt-2 text-xs">
                  La cotation associée n&apos;est pas affectée.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => eventToDelete && deleteEventMutation.mutate(eventToDelete.id)}
              disabled={deleteEventMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteEventMutation.isPending ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              Nouveau dossier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Nom</Label>
              <Input
                id="folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Mon dossier"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) createFolderMutation.mutate();
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex gap-2 flex-wrap">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className="h-7 w-7 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: color,
                      borderColor: newFolderColor === color ? "currentColor" : "transparent",
                      transform: newFolderColor === color ? "scale(1.15)" : "scale(1)",
                    }}
                    onClick={() => setNewFolderColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>Annuler</Button>
            <Button
              onClick={() => createFolderMutation.mutate()}
              disabled={!newFolderName.trim() || createFolderMutation.isPending}
            >
              {createFolderMutation.isPending ? "Création..." : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={!!editingFolder} onOpenChange={(open) => !open && setEditingFolder(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Folder className="h-5 w-5" />
              Modifier le dossier
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="edit-folder-name">Nom</Label>
              <Input
                id="edit-folder-name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Mon dossier"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newFolderName.trim()) updateFolderMutation.mutate();
                }}
              />
            </div>
            <div className="space-y-2">
              <Label>Couleur</Label>
              <div className="flex gap-2 flex-wrap">
                {FOLDER_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className="h-7 w-7 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: color,
                      borderColor: newFolderColor === color ? "currentColor" : "transparent",
                      transform: newFolderColor === color ? "scale(1.15)" : "scale(1)",
                    }}
                    onClick={() => setNewFolderColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingFolder(null)}>Annuler</Button>
            <Button
              onClick={() => updateFolderMutation.mutate()}
              disabled={!newFolderName.trim() || updateFolderMutation.isPending}
            >
              {updateFolderMutation.isPending ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Folder Confirmation */}
      <AlertDialog open={!!folderToDelete} onOpenChange={(open) => !open && setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le dossier ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le dossier &quot;{folderToDelete?.name}&quot; sera supprimé. Les cotations et propositions
              qu&apos;il contient ne seront pas supprimées, elles seront simplement retirées du dossier.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => folderToDelete && deleteFolderMutation.mutate(folderToDelete.id)}
              disabled={deleteFolderMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteFolderMutation.isPending ? "Suppression..." : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
